import BaseController from "sap/mcsuite/controller/BaseController";
import Subtask from "./Subtask";
import Button, { Button$PressEvent } from "sap/m/Button";
import JSONModel from "sap/ui/model/json/JSONModel";
import ODataModelUtils from "./utils/ODataModelUtils";
import Control from "sap/ui/core/Control";
import ManagedObject, { PropertyBindingInfo } from "sap/ui/base/ManagedObject";
import Input from "sap/m/Input";
import DatePicker from "sap/m/DatePicker";
import Select from "sap/m/Select";
import CheckBox from "sap/m/CheckBox";
import PHPBackendRequest from "../handlers/PHPBackendRequest";
import Context from "sap/ui/model/odata/v4/Context";
import MessageToast from "sap/m/MessageToast";
import UI5Form from "sap/ui/layout/form/Form";
import Dialog from "sap/m/Dialog";

export default class Form extends Subtask {
    private formParams: FormCreationParams;
    private fields: Control[] = [];
    private mode = "add"; // can be "add" or "mod"
    private entityModel: JSONModel;
    private validationMessages: string[] = [];

    protected eventHandlers = {
        submit : this.attachSubmitEvent.bind(this),
    };

    /**
     * Creates an instance of List.
     * @param {BaseController} controller the controller that manages this subtask
     * @param {string} subtaskId the ID of the subtask
     * @param {ListCreationParams} params the parameters for list creation, including optional filter and showEmptyTable
     */
    constructor(
        controller: BaseController,
        subtaskId: string,
        params: FormCreationParams,
    ) {
        super(controller, subtaskId);
        this.attachHandlers();
        this.formParams = params;
        this.resetModelChanges();
        this.initTableModel();
        this.initEntityModel();
        this.initObjectModel();
        this.loadFields();
        this.loadValueHelpFields();
    }

    private resetModelChanges(): void {
        this.controller.getODataModel().resetChanges();
    }

    private initTableModel(): void {
        this.controller.getView().setModel(
            new JSONModel({
                table: this.formParams.tableName,
            }),
            "tableName"
        );
    }

    private async initEntityModel(): Promise<void> {
        // When an id is provided, we load the entity from the backend
        const queryModel = this.controller.getView().getModel('query') as JSONModel;
        const entityModel = new JSONModel({});
        this.getSubtaskControl().setModel(entityModel, "entity");
        // If we have an ID in the query model, we load the entity from the backend
        if (queryModel && queryModel.getData().id) {
            const entityId = queryModel.getData().id;
            const entityObject = await ODataModelUtils.requestObject(this.controller.getODataModel(), `/${this.formParams.tableName}(${entityId})`);
            entityModel.setData(entityObject);
            this.mode = "mod";
        } else if (this.formParams.initialData) {
            entityModel.setData(this.formParams.initialData);
        }
        // Apply any query parameters to the entity model, excluding 'id'
        for (const [key, value] of Object.entries(queryModel.getData())) {
            if (key !== "id") {
                entityModel.setProperty(`/${key}`, value);
            }
        }
        this.entityModel = entityModel;
    }

    private async initObjectModel(): Promise<void> {
        const objectModel = new JSONModel(this.formParams.objectField || {});
        this.getSubtaskControl().setModel(objectModel, "object");
    }

    private loadFields(): void {
        const form = this.getUIElementByNameInSubtask("form") as UI5Form;
        const formContainers = form.getFormContainers();
        const formFields = formContainers.flatMap(container => container.getFormElements().flatMap(element => element.getFields()));
        this.fields = formFields;
    }

    private loadValueHelpFields(): void {
        const container = this.getSubtaskControl();
        container.findAggregatedObjects(true,  (element: ManagedObject) => {
            const form = this as Form;
            if (!(element instanceof Input)) return false;
            const input = element as Input;
            if (!(input.getShowValueHelp())) return false;
            const filter = input.data('filter') || null;
            const orderBy = input.data('orderby') || null;
            const title = input.data('title') || null;
            const valueHelpFields: string[] | null =
                input.data('fields')
                    ? input.data('fields')
                        .split(',')
                        .map((f: string) => f.trim())
                        .filter(Boolean)
                    : null;
            input.attachValueHelpRequest(function() {
                const tableName = input.data('table');
                form.controller.showValueHelp(
                    tableName, 
                    input,  
                    null, 
                    null, 
                    title, 
                    null, 
                    filter, 
                    valueHelpFields, 
                    null, 
                    orderBy
                );
            });
        });
    }

    private validateFields(): boolean {
        let allValid = true;
        this.fields.forEach((field) => {
            allValid = allValid && this.validateField(field);
        });
        return allValid;
    }
    private validateField(field: Control): boolean {
        const isMandatory: boolean = field.data("mandatory") === "true" ? true : false;
        let value;
        if (!field.getVisible()) return true; // skip validation for invisible fields
        // switch through the field types we want to support
        switch (field.getMetadata().getName()) {
            case "sap.m.Input":
                value = (field as Input).getValue();
                field = (field as Input);
                break;
            case "sap.m.DatePicker":
                value = (field as DatePicker).getDateValue();
                field = (field as DatePicker);
                break;
            case "sap.m.Select":
                value = (field as Select).getSelectedItemId();
                field = (field as Select);
                break;
            case "sap.m.CheckBox":
                value = (field as CheckBox).getSelected() ? "X" : "";
                field = (field as CheckBox);
                break;
            default:
                console.warn(`Validation for field type ${field.getMetadata().getName()} is not implemented.`);
                return true; // skip validation for unsupported field types
        }
        if (isMandatory && (value === null || value === undefined || value === "")) {
            (field as Input).setValueState("Error");
            (field as Input).setValueStateText("This field is mandatory.");
            return false;
        }
            return true;
    }

    private getSelectValues(): void {
        const selects = this.fields.filter(field => field instanceof Select) as Select[];
        selects.forEach((select) => {
            const selectedItem = select.getSelectedItem();
            if (!selectedItem) return;
            const text = selectedItem.getText();
            const key = selectedItem.getKey();
            const bindingInfo = select.getBindingInfo("selectedKey") as PropertyBindingInfo;
            if (bindingInfo && bindingInfo.parts && bindingInfo.parts.length > 0) {
                let path = (bindingInfo.parts[0] as PropertyBindingInfo).path;
                path = path.split('>').toReversed()[0].split('/').toReversed()[0];
                if (select.data('useSelectedKey')) {
                    this.entityModel.setProperty(path, key);
                } else {
                    this.entityModel.setProperty(path, text);
                }
            }
        });
    }

    private async addCommonData(context: Context): Promise<void> {
        const user = this.controller.getUser();
        // only date, no time
        const now = new Date();
        const object = await context.requestObject();
        if (!object.id) {
            context.setProperty("id", -1);
        }
        /*if (!object.user_create) {
            context.setProperty("user_create", user.getUsername());
        }
        if (!object.date_create) {
            context.setProperty("date_create", now.toISOString().split('T')[0]);
        }*/
        //context.setProperty("user_mod", user.getUsername());
        //context.setProperty("date_mod", now.toISOString().split('T')[0]);
    }

    private attachSubmitEvent(): void {
        const submitButton = this.getUIElementByNameInSubtask("submitButton") as Button;
        if (submitButton) {
            submitButton.attachPress(this.onSubmit.bind(this));
        }
    }

    private resetValidationStates(): void {
        this.fields.forEach((field) => {
            if ("setValueState" in field) {
                (field as Input).setValueState("None");
                (field as Input).setValueStateText("");
            }
        });
    }

    private async sendBackendRequest(entityId: number): Promise<boolean | void> {
        const backendRequest = this.formParams.backendRequest;
        if (!backendRequest.class || !backendRequest.action) return true;
        const request = new PHPBackendRequest(backendRequest.class, backendRequest.action, this.controller.getUser(), {params: JSON.stringify(backendRequest.params)});
        const response = await request.send();
        if (!response.ok) {
            const bodyText = await response.text();
            MessageToast.show(`Error: ${bodyText}`);
            return false;
        }
    }

    private async onSubmit(event: Button$PressEvent): Promise<void> {
        this.resetValidationStates();
        if (!this.validateFields()) {
            return;
        }
        this.getSelectValues();
        let entityId;
        let context: Context;
        // First Case - Add Mode
        if (this.mode === "add") {
            context = ODataModelUtils.createEntity(this.controller.getODataModel(), '/' + this.formParams.tableName, this.getEntityData());
        }
        // Second Case - Mod Mode
        else if (this.mode === "mod") {
            entityId = this.entityModel.getData().id;
            context = this.controller.getODataModel().bindContext(`/${this.formParams.tableName}(${entityId})`).getBoundContext();
            if (!context) {
                throw new Error(`Context for entity ID ${entityId} not found.`);
            }
            for (const key in this.entityModel.getData()) {
                if (key === 'id') continue; // skip the ID field
                context.setProperty(key, this.entityModel.getData()[key]);
            }
        }
        await this.addCommonData(context);
        if (this.formParams.validationFunction && typeof this.formParams.validationFunction === 'function') {
            const validationResult = await this.formParams.validationFunction(context);
            if (validationResult === false) {
                // destroy the created context if in add mode
                if (this.mode === "add") {
                    context.delete();
                }
                return;
            }
        }
        await this.controller.getODataModel().submitBatch('defaultUpdateGroup');
        entityId = await context.requestProperty("id");
        // If a class and action are provided, we send a request to the PHP backend
        const backendValidation = await this.sendBackendRequest(entityId);
        if (backendValidation === false) {
            // @TODO Handle backend validation failure
            return;
        }
        if (this.formParams.submitSuccessCallback && typeof this.formParams.submitSuccessCallback === 'function') {
            this.formParams.submitSuccessCallback();
        }
        const submitButton = event.getSource() as Button;
        if (submitButton.data('close') === 'true') {
            let parent = submitButton.getParent();
            let maxParents = 10; // prevent infinite loop
            while (!(parent instanceof Dialog) && parent && maxParents > 0) {
                parent = parent.getParent();
                maxParents--;
            }
            if (parent instanceof Dialog) {
                parent.close();
                parent.destroy();
            }
        }
        if (submitButton.data('stayOnPage') === 'true') {
            return;
        }
        const module = submitButton.data("module");
        const task = submitButton.data("task");
        if (submitButton.data('field') === 'id') {
            entityId = submitButton.data('value');
        }
        this.controller.navTo(
            "task",
            {
                module: module,
                task: task,
                '?query': {
                    id: entityId
                }
            }
        )
    }

    private getEntityData(): Record<string, any> {
        return this.entityModel.getData();
    }

}

export type FormCreationParams = {
    tableName: string;
    initialData?: Record<string, any>;
    objectField: Record<string, any>;
    backendRequest?: {
        class: string;
        action: string;
        params?: object;
    };
    submitSuccessCallback?: () => void;
    validationFunction?: (context: Context) => boolean | Promise<boolean>;
};
